'use client'

import Box from '@mui/material/Box'

import GenericHero from '@/components/Hero/GenericHero'
import LearnMoreTiles from '@/components/LearnMoreTiles'
import RequestDemo from '@/components/RequestDemo'
import TwoColumnsWithSquareImage from '@/components/TwoColumnsWithSquareImage'
import useDevice from '@/hooks/useDevice'
import LogoSet from '@/sections/EmbedEmissionCalculationLogos'
import EmissionCalcFaq from '@/sections/EmissionCalcFaq'
import EmissionsCsv from '@/sections/EmissionsCsv'
import FintechAnalytics from '@/sections/FintechAnalytics'
import FintechExplanations from '@/sections/FintechExplanations'
import PayhawkCaseStudyBox from '@/sections/PayhawkCaseStudyBox'
import PlugInAPI from '@/sections/PlugInAPI'
import ScaleYourEmissionCalculations from '@/sections/ScaleYourEmissionCalculations'

export default function Page() {
    const { smScreen } = useDevice()
    return (
        <>
            <Box>
                <GenericHero
                    chips={{
                        items: [
                            {
                                src: '/images/heros/chip-embed-emission-calculations-1.png',
                                width: 346,
                                height: 72,
                            },
                            {
                                src: '/images/heros/chip-embed-emission-calculations-2.png',
                                width: 313,
                                height: 72,
                                sx: {
                                    mr: 5,
                                },
                            },
                            {
                                src: '/images/heros/chip-embed-emission-calculations-3.png',
                                width: 316,
                                height: 72,
                                sx: {
                                    ml: 5,
                                },
                            },
                            {
                                src: '/images/heros/chip-embed-emission-calculations-4.png',
                                width: 316,
                                height: 72,
                                sx: {
                                    mr: 5,
                                },
                            },
                        ],
                        gap: 9,
                    }}
                    backgroundImageMobile={'/images/heros/embed-emission-calculations-mobile.png'}
                    title={'Enrich your data with industry-leading emission tracking'}
                    description={`Struggling to differentiate in a crowded market? Capture snowballing demand for sustainability reporting by embedding emission tracking into your offering — effortlessly.`}
                    button={{
                        href: '/request-a-demo',
                        label: 'Request a demo',
                    }}
                />
                <LogoSet />
            </Box>
            <LearnMoreTiles
                tiles={[
                    {
                        title: 'Next-level granularity',
                        description:
                            'Automate scope 3 emissions calculations to help your clients get regulation-ready.',
                        scrollToSection: 'granular',
                        icon: '/icons/icon-granular.png',
                    },
                    {
                        title: (
                            <>
                                Auditable calculation <br />
                                breakdowns
                            </>
                        ),
                        description:
                            'Understand how calculations were made even if you’re not a climate scientist.',
                        scrollToSection: 'explanations',
                        icon: '/icons/icon-accredited.png',
                    },
                    {
                        title: <>Out-of-the-box reporting</>,
                        description:
                            'Accelerate your customers decarbonisation goals with decision-useful reports.',
                        scrollToSection: 'analytics',
                        icon: '/icons/icon-grow-users.png',
                    },
                    {
                        title: (
                            <>
                                Scalable <br /> and accessible
                            </>
                        ),
                        description:
                            'Plug in and go with a simple REST API, or get immediate results by uploading a spreadsheet.',
                        scrollToSection: 'plug-in-api',
                        icon: '/icons/icon-scalable.png',
                    },
                ]}
            />
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: smScreen ? 0 : 20,
                }}
            >
                <PayhawkCaseStudyBox />
            </Box>
            <Box id={'animation'}>
                <ScaleYourEmissionCalculations />
            </Box>
            <Box id={'granular'}>
                <TwoColumnsWithSquareImage
                    title={'Granular, audited, and industry-leading emission tracking'}
                    paragraph={
                        <>
                            Cement your reputation as an industry-leader by enriching your data with
                            best-in-class emission tracking.
                            <br />
                            <br />
                            Lune’s calculation methodology is anchored in the GHG Protocol, and
                            audited and certified by ISO so you can equip your customers with the
                            granular calculations they need to get regulation-ready.
                        </>
                    }
                    image={{
                        src: '/images/logo-grids/logos-ghg-iso-glec.png',
                        alt: 'Greenhouse Gas Protocol logos',
                    }}
                />
            </Box>
            <Box id={'explanations'}>
                <FintechExplanations />
            </Box>
            <Box id={'analytics'}>
                <FintechAnalytics />
            </Box>
            <Box id={'plug-in-api'}>
                <PlugInAPI />
            </Box>
            <EmissionsCsv />
            <EmissionCalcFaq />
            <RequestDemo />
        </>
    )
}
